/**
 * #1428 — stop asking for KeyPackages a device does not have.
 *
 * Every delivery pull walks each group's pending Welcome requests and calls
 * `GET /mls/key-packages/batch` for the requesting devices. Measured on loopita
 * 2026-10-24: four owner browsers had been closed for a day with EMPTY pools,
 * so every pull (~15 s) asked the batch for all four, got nothing, and logged
 *
 *   [SecureChannel] 4 pending Welcome requests for shared group 10440bf7   (stdout)
 *   [SecureChannel] No KeyPackage for a7f7eada, skipping                     (stderr, x4)
 *
 * — 50,760 lines in one log. Nothing was lost: a Welcome is sealed to a
 * KeyPackage, so nobody can admit a device until it comes back online and
 * republishes. The asking just has to stop until then.
 *
 * Per (group, device):
 *   - a batch that returns no KeyPackage starts a backoff: 10 min, doubling to
 *     a 1 h cap. One warn when it starts; silent while it runs.
 *   - a KeyPackage that appears ends it, with one info line ("admitting").
 *   - the request disappearing (served elsewhere, expired) ends it silently.
 *
 * Coming back must not wait out the backoff. A reopened browser republishes and
 * re-requests, but its re-request dedups onto the SAME pending row server-side,
 * so the pending list alone cannot show it. The server now says per request
 * whether the batch would serve a KeyPackage (`key_package_available`, #1428
 * backend). `false` means "do not ask" outright; a flip to `true` means "ask on
 * this pull", whatever the backoff says. When the field is absent (an older
 * backend) the time-based backoff is the whole rule.
 *
 * Also: "N pending Welcome requests" is logged only when the pending SET
 * changes, and a non-OK fetch is logged once (and once on recovery) instead of
 * being a silent `continue`.
 */

export const KP_BACKOFF_INITIAL_MS = 10 * 60_000;
export const KP_BACKOFF_MAX_MS = 60 * 60_000;
/**
 * #1429 review: the server said `key_package_available: true` and the batch
 * still came back empty — a race (another fulfiller claimed the last one, or
 * the pool expired between the two reads). That is not "the device is gone", so
 * it gets a short fixed retry, not the 10 -> 60 min ladder. Bounded: after
 * KP_RACE_RETRY_LIMIT such retries in a row the ordinary backoff takes over, so
 * a server that persistently disagrees with itself costs one call per ladder
 * step, not one a minute forever.
 */
export const KP_RACE_RETRY_MS = 60_000;
export const KP_RACE_RETRY_LIMIT = 3;

export interface PendingWelcomeRow {
  id: string;
  requesting_device_id: string;
  /** #1428 backend: would the batch serve this caller a KeyPackage for it? */
  key_package_available?: boolean;
}

interface Entry {
  untilMs: number;
  delayMs: number;
  /** What the server last said about availability; undefined = never said. */
  lastAvailable: boolean | undefined;
  /** Consecutive "server said yes, batch said no" retries (#1429). */
  raceRetries: number;
}

export interface BackoffLogger {
  warn(msg: string): void;
  info(msg: string): void;
}

const consoleLogger: BackoffLogger = {
  warn: (m) => console.warn(m),
  info: (m) => console.log(m),
};

const short = (id: string) => id.slice(0, 8);

export class WelcomeKeyPackageBackoff {
  private readonly entries = new Map<string, Entry>();
  private readonly pendingSig = new Map<string, string>();
  private readonly failing = new Set<string>();

  constructor(private readonly log: BackoffLogger = consoleLogger) {}

  private key(groupId: string, deviceId: string): string {
    return `${groupId}:${deviceId}`;
  }

  /**
   * Record this pull's pending list for a group. Returns true when the set of
   * requests differs from the previous pull's — the caller logs its "N pending"
   * line only then. Backoffs for devices whose request is gone are dropped.
   */
  observePending(groupId: string, pending: PendingWelcomeRow[]): boolean {
    const sig = pending.map((r) => r.id).sort().join(",");
    const changed = this.pendingSig.get(groupId) !== sig;
    if (pending.length === 0) this.pendingSig.delete(groupId);
    else this.pendingSig.set(groupId, sig);

    const live = new Set(pending.map((r) => r.requesting_device_id));
    const prefix = `${groupId}:`;
    for (const k of [...this.entries.keys()]) {
      if (k.startsWith(prefix) && !live.has(k.slice(prefix.length))) this.entries.delete(k);
    }
    return changed && pending.length > 0;
  }

  /** Should this pull ask the batch for this device's KeyPackage? */
  shouldAsk(
    groupId: string,
    deviceId: string,
    available: boolean | undefined,
    label: string,
    now: number = Date.now(),
  ): boolean {
    const k = this.key(groupId, deviceId);
    const entry = this.entries.get(k);
    if (available === false) {
      // The server says there is nothing to serve: never ask, whatever the clock.
      if (!entry) {
        this.entries.set(k, { untilMs: now + KP_BACKOFF_INITIAL_MS, delayMs: KP_BACKOFF_INITIAL_MS, lastAvailable: false, raceRetries: 0 });
        this.log.warn(
          `[SecureChannel] No KeyPackage for ${short(deviceId)} in ${label} — waiting for the device ` +
            `to come back online; not asking again until it publishes one`,
        );
      } else {
        entry.lastAvailable = false;
      }
      return false;
    }
    if (!entry) return true;
    // It came back: the server now has one to serve. Ask now, not in an hour.
    if (available === true && entry.lastAvailable === false) return true;
    return now >= entry.untilMs;
  }

  /** The outcome of an ask. `found` = the batch returned a KeyPackage. */
  recordKeyPackage(
    groupId: string,
    deviceId: string,
    found: boolean,
    available: boolean | undefined,
    label: string,
    now: number = Date.now(),
  ): void {
    const k = this.key(groupId, deviceId);
    const entry = this.entries.get(k);
    if (found) {
      if (entry) {
        this.entries.delete(k);
        this.log.info(`[SecureChannel] KeyPackage for ${short(deviceId)} is available again in ${label} — admitting`);
      }
      return;
    }
    // The server said it had one: a race, not an absent device (#1429).
    const raced = available === true && (entry?.raceRetries ?? 0) < KP_RACE_RETRY_LIMIT;
    if (!entry) {
      if (raced) {
        this.entries.set(k, { untilMs: now + KP_RACE_RETRY_MS, delayMs: 0, lastAvailable: available, raceRetries: 1 });
        this.log.warn(
          `[SecureChannel] No KeyPackage for ${short(deviceId)} in ${label} although the server listed one ` +
            `(claimed elsewhere?) — retrying in ${KP_RACE_RETRY_MS / 1000} s`,
        );
        return;
      }
      this.entries.set(k, { untilMs: now + KP_BACKOFF_INITIAL_MS, delayMs: KP_BACKOFF_INITIAL_MS, lastAvailable: available, raceRetries: 0 });
      this.log.warn(
        `[SecureChannel] No KeyPackage for ${short(deviceId)} in ${label}, skipping — ` +
          `not asking again for ${KP_BACKOFF_INITIAL_MS / 60_000} min (the device has to come back online)`,
      );
      return;
    }
    entry.lastAvailable = available;
    if (raced) {
      entry.raceRetries += 1;
      entry.untilMs = now + KP_RACE_RETRY_MS;
      return;
    }
    entry.raceRetries = available === true ? entry.raceRetries : 0;
    entry.delayMs = entry.delayMs === 0 ? KP_BACKOFF_INITIAL_MS : Math.min(entry.delayMs * 2, KP_BACKOFF_MAX_MS);
    entry.untilMs = now + entry.delayMs;
  }

  /** A non-OK response for `scope`: warn once until it recovers. */
  noteFetchFailure(scope: string, status: number | string): void {
    if (this.failing.has(scope)) return;
    this.failing.add(scope);
    this.log.warn(`[SecureChannel] ${scope} failed (${status}) — will retry each pull; logging once until it recovers`);
  }

  noteFetchOk(scope: string): void {
    if (!this.failing.delete(scope)) return;
    this.log.info(`[SecureChannel] ${scope} recovered`);
  }

  /** Test/diagnostic view. */
  backoffFor(groupId: string, deviceId: string): { untilMs: number; delayMs: number } | undefined {
    const e = this.entries.get(this.key(groupId, deviceId));
    return e ? { untilMs: e.untilMs, delayMs: e.delayMs } : undefined;
  }
}
